import { useState } from "react";
import { rankOf, PASS_LABEL } from "../data/levels.js";
import { stamp } from "../lib/storage.js";
import { openLink } from "../lib/links.js";

/**
 * An external link that isn't rendered below its required tier.
 *
 * The tier here decides whether the card is drawn and nothing more. The card
 * carries a link id, not an address — /api/links holds the URL and re-checks
 * the tier before handing it over, so hiding the card is now a courtesy rather
 * than the security boundary it was being asked to be.
 */
export function LockedCard({ item, userRank, icon }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (userRank < rankOf(item.minLevel)) return null;

  const open = async () => {
    if (busy) return;
    setBusy(true);
    setNote(await openLink(item.id));
    setBusy(false);
  };

  return (
    <button className="card" onClick={open} disabled={busy}>
      <div className="name">
        <span className="lead">
          {icon && <span>{item.icon}</span>}
          <span>{item.name}</span>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {rankOf(item.minLevel) > 0 && (
            <span className="badge">{PASS_LABEL[item.minLevel].split(" ")[0]}</span>
          )}
          <span className="arrow">→</span>
        </span>
      </div>
      <div className="desc">{item.desc}</div>
      {note && <div className="card-note">{note}</div>}
    </button>
  );
}

/**
 * A card that opens an external link by id.
 *
 * Every call site had its own `<a href={item.url}>`, which is how forty
 * addresses ended up compiled into a bundle anybody can read. There is no href
 * now: the id goes to /api/links, the server checks the tier, and the browser
 * is sent on only if it should be.
 */
export function ExternalLink({ id, className = "card", children, title }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  return (
    <button
      className={className}
      title={title}
      disabled={busy}
      onClick={async () => {
        if (busy) return;
        setBusy(true);
        setNote(await openLink(id));
        setBusy(false);
      }}
    >
      {children}
      {note && <div className="card-note">{note}</div>}
    </button>
  );
}

// In-app navigation card.
export function Row({ onGo, name, desc, icon }) {
  return (
    <button className="card" onClick={onGo}>
      <div className="name">
        <span className="lead">
          {icon && <span>{icon}</span>}
          <span>{name}</span>
        </span>
        <span className="arrow">→</span>
      </div>
      {desc && <div className="desc">{desc}</div>}
    </button>
  );
}

// A titled block on the home feed.
export function Section({ eyebrow, title, sub, children, keepCase }) {
  return (
    <section>
      <div className="eyebrow">{eyebrow}</div>
      <h2 className={"display" + (keepCase ? " keepcase" : "")}>{title}</h2>
      {sub && <p className="sub">{sub}</p>}
      {children}
    </section>
  );
}

// Full-screen sub-page shell with the back affordance.
//
// `tone` adds a class to the shell so a page can re-theme the shared furniture
// (head, back button, chips) without forking this component. Analytics uses it
// to go dark; every other page leaves it unset and stays on paper.
export function Page({ back, eyebrow, title, intro, children, keepCase, tone }) {
  return (
    <div className={"above" + (tone ? " " + tone : "")}>
      <div className="page-head">
        <button className="backbtn" onClick={back}>
          ← Back to the hub
        </button>
        {/* A page that brings its own hero passes neither, and gets neither —
            an empty eyebrow would still draw its accent bar. */}
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        {title && <h1 className={"display" + (keepCase ? " keepcase" : "")}>{title}</h1>}
        {intro && <p className="intro">{intro}</p>}
      </div>
      <div className="wrap">{children}</div>
    </div>
  );
}

export function Skeleton() {
  return (
    <div aria-hidden="true">
      <div className="skel" style={{ width: "72%" }} />
      <div className="skel" style={{ width: "92%" }} />
      <div className="skel" style={{ width: "55%" }} />
    </div>
  );
}

const KIND_LABEL = {
  testimony: "🔥 Testimony",
  prayer: "🙏 Prayer request",
  meeting: "🤝 Meeting request",
  checkin: "💛 Check-in",
};

// Newest first.
export function MessageList({ items, empty, kindTag }) {
  if (!items.length) return <div className="empty-note">{empty}</div>;
  return (
    <div>
      {items
        .slice()
        .reverse()
        .map((m, i) => (
          <div key={m.ts + "-" + i} className={"msg" + (kindTag && m.kind ? " " + m.kind : "")}>
            <span className="mname">{m.name || "Anonymous"}</span>
            <span className="mtime">{stamp(m.ts)}</span>
            {kindTag && m.kind && (
              <span className="mtime">· {KIND_LABEL[m.kind] || m.kind}</span>
            )}
            <div className="mtext">{m.text}</div>
          </div>
        ))}
    </div>
  );
}

export function Composer({ placeholder, onPost, busyLabel = "Post", children }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    await onPost(text.trim());
    setText("");
    setBusy(false);
  };

  return (
    <div className="composer">
      <textarea
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="crow">
        {children || <span />}
        <button className="btn-post" onClick={submit} disabled={!text.trim() || busy}>
          {busy ? "Posting…" : busyLabel}
        </button>
      </div>
    </div>
  );
}
